import { useCallback, useEffect, useRef, useState } from 'react';

import {
    getColumnDealIds,
    getColumnIds,
    getColumnIndexById,
    getDealLocation,
    getSourceBoardColumnId,
    getSourceDealId,
    getTargetBoardColumnId,
    getTargetColumnId,
    getTargetDealId,
    getTargetTerminalBucketKey,
    moveColumn,
    moveDeal,
} from '@crm/pages/deals/lib';

import { useGetDealsBoardQuery } from '../../api';

import type { TColumnDragEndResult, TDealDragEndResult } from './types';
import type { TDealColumn } from '@crm/entities/deals-board';
import type { DragEndEvent, DragOverEvent, DragStartEvent } from '@dnd-kit/react';

type TColumnsUpdater = TDealColumn[] | ((currentColumns: TDealColumn[]) => TDealColumn[]);

export const useDealsBoardDnd = () => {
    const dealsBoardQuery = useGetDealsBoardQuery();
    const [columns, setColumns] = useState<TDealColumn[]>([]);
    const [activeDealId, setActiveDealId] = useState<string | null>(null);
    const [isTerminalBucketOver, setIsTerminalBucketOver] = useState(false);
    const columnsRef = useRef<TDealColumn[]>([]);
    const columnsSnapshotRef = useRef<TDealColumn[]>([]);
    const dragOperationIdRef = useRef(0);
    const pendingDealDragCleanupRef = useRef<(() => void) | null>(null);

    const updateColumns = (updater: TColumnsUpdater) => {
        setColumns((currentColumns) => {
            const nextColumns = typeof updater === 'function' ? updater(currentColumns) : updater;

            columnsRef.current = nextColumns;
            return nextColumns;
        });
    };

    const rollbackColumns = (dragOperationId: number) => {
        if (dragOperationIdRef.current !== dragOperationId) {
            return;
        }

        updateColumns(columnsSnapshotRef.current);
    };

    const completeDealDragCleanup = useCallback(() => {
        pendingDealDragCleanupRef.current?.();
        pendingDealDragCleanupRef.current = null;
        setActiveDealId(null);
        setIsTerminalBucketOver(false);
    }, []);

    useEffect(() => {
        if (dealsBoardQuery.data?.columns) {
            updateColumns(dealsBoardQuery.data.columns);
        }
    }, [dealsBoardQuery.data?.columns]);

    const handleDragStart = (event: DragStartEvent) => {
        const sourceDealId = getSourceDealId(event.operation.source);

        pendingDealDragCleanupRef.current = null;
        dragOperationIdRef.current += 1;
        columnsSnapshotRef.current = columnsRef.current;
        setActiveDealId(sourceDealId ?? null);
        setIsTerminalBucketOver(false);
    };

    const handleDragOver = (event: DragOverEvent) => {
        const { source, target } = event.operation;

        if (!source || !target) {
            setIsTerminalBucketOver(false);
            return;
        }

        const sourceColumnId = getSourceBoardColumnId(source);

        if (sourceColumnId) {
            setIsTerminalBucketOver(false);
            const targetColumnId = getTargetBoardColumnId(target);

            if (!targetColumnId) {
                return;
            }

            updateColumns((currentColumns) =>
                moveColumn(currentColumns, sourceColumnId, targetColumnId)
            );
            return;
        }

        const sourceDealId = getSourceDealId(source);

        if (!sourceDealId) {
            setIsTerminalBucketOver(false);
            return;
        }

        const targetTerminalBucketKey = getTargetTerminalBucketKey(target);

        if (targetTerminalBucketKey) {
            setIsTerminalBucketOver(true);
            updateColumns(columnsSnapshotRef.current);
            return;
        }

        setIsTerminalBucketOver(false);

        const targetColumnId = getTargetColumnId(target);

        if (!targetColumnId) {
            return;
        }

        updateColumns((currentColumns) => {
            const columnsWithSourceDeal =
                getDealLocation(currentColumns, sourceDealId) !== null
                    ? currentColumns
                    : columnsSnapshotRef.current;

            return moveDeal(
                columnsWithSourceDeal,
                sourceDealId,
                targetColumnId,
                getTargetDealId(target)
            );
        });
    };

    const getDealDragEndResult = (event: DragEndEvent): TDealDragEndResult | undefined => {
        const dragOperationId = dragOperationIdRef.current;

        if (event.canceled || !event.operation.target) {
            return undefined;
        }

        const { source } = event.operation;
        const sourceDealId = getSourceDealId(source);

        if (!sourceDealId) {
            return undefined;
        }

        const previousDealLocation = getDealLocation(columnsSnapshotRef.current, sourceDealId);
        const currentDealLocation = getDealLocation(columnsRef.current, sourceDealId);

        if (!previousDealLocation || !currentDealLocation) {
            return undefined;
        }

        if (
            previousDealLocation.columnIndex === currentDealLocation.columnIndex &&
            previousDealLocation.dealIndex === currentDealLocation.dealIndex
        ) {
            return undefined;
        }

        const targetColumn = columnsRef.current[currentDealLocation.columnIndex];
        const movedDeal = targetColumn.deals[currentDealLocation.dealIndex];

        if (!movedDeal) {
            return undefined;
        }

        return {
            isColumnChanged: previousDealLocation.columnIndex !== currentDealLocation.columnIndex,
            payload: {
                dealId: movedDeal.id,
                targetColumnId: targetColumn.id,
                targetDealIds: getColumnDealIds(targetColumn),
            },
            targetColumnTitle: targetColumn.title,
            rollback: () => rollbackColumns(dragOperationId),
        };
    };

    const getColumnDragEndResult = (event: DragEndEvent): TColumnDragEndResult | undefined => {
        const dragOperationId = dragOperationIdRef.current;

        if (event.canceled || !event.operation.target) {
            return undefined;
        }

        const { source } = event.operation;
        const sourceColumnId = getSourceBoardColumnId(source);

        if (!sourceColumnId) {
            return undefined;
        }

        const previousColumnIndex = getColumnIndexById(columnsSnapshotRef.current, sourceColumnId);
        const currentColumnIndex = getColumnIndexById(columnsRef.current, sourceColumnId);

        if (previousColumnIndex === -1 || currentColumnIndex === -1) {
            return undefined;
        }

        if (previousColumnIndex === currentColumnIndex) {
            return undefined;
        }

        return {
            payload: {
                targetColumnIds: getColumnIds(columnsRef.current),
            },
            rollback: () => rollbackColumns(dragOperationId),
        };
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const dragOperationId = dragOperationIdRef.current;
        const sourceDealId = getSourceDealId(event.operation.source);
        const shouldRollback =
            event.canceled ||
            !event.operation.target ||
            (sourceDealId !== undefined &&
                getDealLocation(columnsRef.current, sourceDealId) === null);

        if (sourceDealId === undefined) {
            setActiveDealId(null);
            setIsTerminalBucketOver(false);

            if (shouldRollback) {
                rollbackColumns(dragOperationId);
            }
            return;
        }

        pendingDealDragCleanupRef.current = shouldRollback
            ? () => rollbackColumns(dragOperationId)
            : null;
    };

    return {
        columns,
        activeDealId,
        dealsBoardQuery,
        getColumnDragEndResult,
        getDealDragEndResult,
        completeDealDragCleanup,
        handleDragEnd,
        handleDragOver,
        handleDragStart,
        isTerminalBucketOver,
    };
};
